import type { SelectOption } from '~/components/ui/inputs/selectOptions';
import type {
  BuyerInventoryFiltersInput,
  UserPreferenceInput,
} from '~/graphql/graphql';
import { hasText } from '~/utils';
import {
  branchCodeOf,
  groupLocationsByBranch,
  type BranchCoded,
} from '~/utils/locationBranch';

export type BranchSelection =
  | { kind: 'all' }
  | { kind: 'branch'; branchCode: string }
  | { kind: 'location'; locationId: string };

type BranchedLocation = BranchCoded & { id: string };

type BranchOptionLabels<T> = {
  all: string;
  noBranchCode: string;
  branch: (branchCode: string, locationCount: number) => string;
  singleLocationBranch: (branchCode: string, location: T) => string;
  location: (location: T) => string;
};

export const ALL_BRANCHES: BranchSelection = { kind: 'all' };
const ALL_VALUE = 'all';
const BRANCH_PREFIX = 'branch:';
const LOCATION_PREFIX = 'location:';

export function serializeBranchSelection(selection: BranchSelection): string {
  if (selection.kind === 'branch') return BRANCH_PREFIX + selection.branchCode;
  if (selection.kind === 'location')
    return LOCATION_PREFIX + selection.locationId;
  return ALL_VALUE;
}

export function parseBranchSelection(value: string): BranchSelection {
  if (value.startsWith(BRANCH_PREFIX))
    return { kind: 'branch', branchCode: value.slice(BRANCH_PREFIX.length) };
  if (value.startsWith(LOCATION_PREFIX))
    return {
      kind: 'location',
      locationId: value.slice(LOCATION_PREFIX.length),
    };
  return ALL_BRANCHES;
}

export function branchSelectOptions<T extends BranchedLocation>(
  locations: T[],
  labels: BranchOptionLabels<T>
): SelectOption[] {
  const groups = groupLocationsByBranch(locations);
  const unbranched =
    groups.find(({ branchCode }) => branchCode === null)?.locations ?? [];

  const branchOptions = groups
    .flatMap(({ branchCode, locations: branchLocations }) =>
      branchCode === null
        ? []
        : [
            {
              value: serializeBranchSelection({ kind: 'branch', branchCode }),
              label:
                branchLocations.length === 1
                  ? labels.singleLocationBranch(branchCode, branchLocations[0])
                  : labels.branch(branchCode, branchLocations.length),
            },
          ]
    )
    .sort((a, b) => a.label.localeCompare(b.label));
  const group = branchOptions.length > 0 ? labels.noBranchCode : undefined;
  const locationOptions = unbranched.map((location) => ({
    value: serializeBranchSelection({
      kind: 'location',
      locationId: location.id,
    }),
    label: labels.location(location),
    ...(group ? { group } : {}),
  }));

  return [
    { value: ALL_VALUE, label: labels.all },
    ...branchOptions,
    ...locationOptions,
  ];
}

export function assignedLocations<T extends { id: string }>(
  locations: T[],
  assigned: ReadonlyArray<{ id: string }>
): T[] {
  const assignedIds = new Set(assigned.map(({ id }) => id));
  return locations.filter(({ id }) => assignedIds.has(id));
}

function locationsInBranch<T extends BranchedLocation>(
  locations: T[],
  branchCode: string
): T[] {
  return locations.filter((location) => branchCodeOf(location) === branchCode);
}

export function availableBranchSelection(
  selection: BranchSelection,
  locations: BranchedLocation[]
): BranchSelection {
  if (selection.kind === 'branch')
    return locationsInBranch(locations, selection.branchCode).length > 0
      ? selection
      : ALL_BRANCHES;
  if (selection.kind === 'location') {
    const location = locations.find(({ id }) => id === selection.locationId);
    if (!location) return ALL_BRANCHES;
    const branchCode = branchCodeOf(location);
    return branchCode === null ? selection : { kind: 'branch', branchCode };
  }
  return selection;
}

export function filterForBranchSelection(
  selection: BranchSelection,
  locations: BranchedLocation[]
): BuyerInventoryFiltersInput {
  const available = availableBranchSelection(selection, locations);
  if (available.kind === 'branch')
    return {
      locationIds: {
        in: locationsInBranch(locations, available.branchCode).map(
          ({ id }) => id
        ),
      },
    };
  if (available.kind === 'location')
    return { locationId: { eq: available.locationId } };
  return {};
}

export function selectionFromPreferences(
  saved: { branchCode?: string | null; locationId?: string | null },
  locations: BranchedLocation[]
): BranchSelection {
  const savedBranchCode = branchCodeOf(saved);
  if (savedBranchCode !== null)
    return availableBranchSelection(
      { kind: 'branch', branchCode: savedBranchCode },
      locations
    );
  return hasText(saved.locationId)
    ? availableBranchSelection(
        { kind: 'location', locationId: saved.locationId },
        locations
      )
    : ALL_BRANCHES;
}

export function preferencesForBranchSelection(
  selection: BranchSelection
): Pick<
  UserPreferenceInput,
  'buyerInventorySelectedBranchCode' | 'buyerInventorySelectedLocationId'
> {
  return {
    buyerInventorySelectedBranchCode:
      selection.kind === 'branch' ? selection.branchCode : '',
    buyerInventorySelectedLocationId:
      selection.kind === 'location' ? selection.locationId : '',
  };
}
